import { createSetDataStore, parseSetDataPath } from './set-data.js';
import { RuntimeInstanceError } from './instance-factory.js';
const UNSAFE_KEYS = new Set(['__proto__', 'constructor', 'prototype']);
const COMPONENT_INSTANCE_KEYS = new Set([
    'attach',
    'data',
    'detach',
    'lifecycleState',
    'methods',
    'pageHide',
    'pageShow',
    'properties',
    'ready',
    'setData',
    'triggerEvent',
    'updateProperties',
    'whenSettled',
]);
const PROPERTY_TYPES = new Set([
    String,
    Number,
    Boolean,
    Object,
    Array,
    null,
]);
const MAX_DATA_DEPTH = 100;
function definitionError(message, remediation) {
    return new RuntimeInstanceError('WF7201', message, remediation);
}
function unsupportedOption(key) {
    return new RuntimeInstanceError('WF7202', `Component option ${key} is outside the implemented runtime subset or conflicts with the instance facade`, `Remove ${key} or implement its cataloged component runtime capability before compiling this component.`);
}
function transitionError(operation, state) {
    return new RuntimeInstanceError('WF7203', `Cannot ${operation} component while it is ${state}`, 'Let the generated component wrapper drive attached, ready, detached, and property-update order.');
}
function isPlainRecord(value) {
    if (value === null || typeof value !== 'object' || Array.isArray(value))
        return false;
    const prototype = Object.getPrototypeOf(value);
    return prototype === null || prototype === Object.prototype;
}
function dataValue(object, key, label) {
    const descriptor = Object.getOwnPropertyDescriptor(object, key);
    if (descriptor === undefined)
        return { exists: false, value: undefined };
    if (!('value' in descriptor)) {
        throw definitionError(`${label} contains an accessor property: ${key}`, `Replace the ${key} getter or setter with a direct data property.`);
    }
    return { exists: true, value: descriptor.value };
}
function cloneValue(value, path, ancestors = new Set(), depth = 0) {
    if (value === null
        || typeof value === 'boolean'
        || typeof value === 'string'
        || (typeof value === 'number' && Number.isFinite(value)))
        return value;
    if (depth > MAX_DATA_DEPTH) {
        throw definitionError(`Component data exceeds the supported nesting depth at ${path}`, `Keep component data at or below ${MAX_DATA_DEPTH} nested containers.`);
    }
    if (typeof value !== 'object') {
        throw definitionError(`Component data contains a non-serializable value at ${path}: ${typeof value}`, 'Use null, booleans, finite numbers, strings, arrays, and plain objects.');
    }
    if (!Array.isArray(value) && !isPlainRecord(value)) {
        throw definitionError(`Component data contains a non-plain object at ${path}`, 'Convert dates, maps, sets, host objects, and class instances into JSON-like data.');
    }
    if (ancestors.has(value)) {
        throw definitionError(`Component data contains a cycle at ${path}`, 'Remove circular references from component data and property defaults.');
    }
    ancestors.add(value);
    const clone = Array.isArray(value)
        ? new Array(value.length)
        : {};
    for (const key of Object.keys(value)) {
        if (UNSAFE_KEYS.has(key)) {
            throw definitionError(`Component data contains an unsafe key at ${path}.${key}`, 'Rename __proto__, prototype, and constructor fields.');
        }
        const current = dataValue(value, key, path);
        Object.defineProperty(clone, key, {
            configurable: true,
            enumerable: true,
            value: cloneValue(current.value, `${path}.${key}`, ancestors, depth + 1),
            writable: true,
        });
    }
    ancestors.delete(value);
    return clone;
}
function cloneRoot(value, label) {
    if (!isPlainRecord(value)) {
        throw definitionError(`${label} must be a plain object`, `Initialize ${label} with a JSON-like object.`);
    }
    return cloneValue(value, label);
}
function snapshotOptions(options) {
    const snapshot = Object.create(null);
    for (const key of Object.keys(options)) {
        const current = dataValue(options, key, 'Component options');
        Object.defineProperty(snapshot, key, {
            configurable: false,
            enumerable: true,
            value: current.value,
            writable: false,
        });
    }
    return Object.freeze(snapshot);
}
function defaultForType(type) {
    if (type === String)
        return '';
    if (type === Number)
        return 0;
    if (type === Boolean)
        return false;
    return null;
}
function sameDataValue(left, right) {
    if (Object.is(left, right))
        return true;
    if (left === null || right === null || typeof left !== 'object' || typeof right !== 'object') {
        return false;
    }
    if (Array.isArray(left) || Array.isArray(right)) {
        if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length)
            return false;
    }
    else if (!isPlainRecord(left) || !isPlainRecord(right)) {
        return false;
    }
    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);
    if (leftKeys.length !== rightKeys.length)
        return false;
    for (const key of leftKeys) {
        if (!Object.prototype.hasOwnProperty.call(right, key))
            return false;
        if (!sameDataValue(left[key], right[key]))
            return false;
    }
    return true;
}
function normalizeProperties(value) {
    if (value === undefined)
        return Object.freeze([]);
    if (!isPlainRecord(value)) {
        throw definitionError('Component.properties must be a plain object', 'Declare each component property with a constructor or a { type, value } object.');
    }
    const properties = [];
    for (const name of Object.keys(value)) {
        if (UNSAFE_KEYS.has(name)) {
            throw definitionError(`Component property uses an unsafe name: ${name}`, 'Rename __proto__, prototype, and constructor properties.');
        }
        const declaration = dataValue(value, name, 'Component.properties').value;
        let type;
        let defaultValue;
        let observer = null;
        if (PROPERTY_TYPES.has(declaration)) {
            type = declaration;
            defaultValue = defaultForType(type);
        }
        else if (isPlainRecord(declaration)) {
            for (const key of Object.keys(declaration)) {
                if (key !== 'observer' && key !== 'type' && key !== 'value') {
                    throw unsupportedOption(`properties.${name}.${key}`);
                }
            }
            const declaredType = dataValue(declaration, 'type', `Component.properties.${name}`);
            type = declaredType.exists ? declaredType.value : null;
            if (!PROPERTY_TYPES.has(type)) {
                throw definitionError(`Component property ${name} has an unsupported type declaration`, 'Use String, Number, Boolean, Object, Array, or null as the property type.');
            }
            const declaredDefault = dataValue(declaration, 'value', `Component.properties.${name}`);
            defaultValue = declaredDefault.exists ? declaredDefault.value : defaultForType(type);
            const declaredObserver = dataValue(declaration, 'observer', `Component.properties.${name}`);
            if (declaredObserver.exists) {
                if (typeof declaredObserver.value !== 'function') {
                    throw definitionError(`Component property ${name} observer must be a function`, `Replace properties.${name}.observer with a function accepting newValue, oldValue, and changedPath.`);
                }
                observer = declaredObserver.value;
            }
        }
        else {
            throw definitionError(`Component property ${name} has an invalid declaration`, 'Use a constructor or a plain { type, value } declaration.');
        }
        properties.push(Object.freeze({
            defaultValue: cloneValue(defaultValue, `Component.properties.${name}.value`),
            name,
            observer,
            type,
        }));
    }
    return Object.freeze(properties);
}
function normalizeObservers(value) {
    if (value === undefined)
        return Object.freeze([]);
    if (!isPlainRecord(value)) {
        throw definitionError('Component.observers must be a plain object', 'Declare exact single-path component observers in one plain object.');
    }
    const observers = [];
    for (const path of Object.keys(value)) {
        if (path.includes(',') || path.includes('*')) {
            throw unsupportedOption(`observers.${path}`);
        }
        const method = dataValue(value, path, 'Component.observers').value;
        if (typeof method !== 'function') {
            throw definitionError(`Component observer ${path} must be a function`, `Replace observers.${path} with a function accepting the changed value.`);
        }
        observers.push(Object.freeze({
            method: method,
            path,
            segments: parseSetDataPath(path),
        }));
    }
    return Object.freeze(observers);
}
function valueAtPath(data, segments) {
    let current = data;
    for (const segment of segments) {
        if (current === null || typeof current !== 'object')
            return undefined;
        const descriptor = Object.getOwnPropertyDescriptor(current, segment);
        if (descriptor === undefined)
            return undefined;
        if (!('value' in descriptor)) {
            throw definitionError(`Component observer cannot read an accessor at ${String(segment)}`, 'Keep observed component data JSON-like and free of getters and setters.');
        }
        current = descriptor.value;
    }
    return current;
}
function snapshotObservedValue(value, path) {
    return value === undefined ? undefined : cloneValue(value, `Component.observers.${path}`);
}
function normalizePropertyValue(property, value) {
    if (value === undefined)
        return cloneValue(property.defaultValue, `Component.properties.${property.name}`);
    if (property.type === String)
        return typeof value === 'string' ? value : String(value);
    if (property.type === Number) {
        const number = typeof value === 'number' ? value : Number(value);
        if (Number.isFinite(number))
            return number;
    }
    else if (property.type === Boolean) {
        if (value === '' || value === true)
            return true;
        if (value === false || value === 'false')
            return false;
        return Boolean(value);
    }
    else if (property.type === Array) {
        if (Array.isArray(value))
            return cloneValue(value, `Component.properties.${property.name}`);
    }
    else if (property.type === Object) {
        if (value === null || isPlainRecord(value)) {
            return cloneValue(value, `Component.properties.${property.name}`);
        }
    }
    else if (property.type === null) {
        return cloneValue(value, `Component.properties.${property.name}`);
    }
    throw definitionError(`Component property ${property.name} received a value incompatible with its declared type`, `Pass a value compatible with the ${property.type?.name ?? 'untyped'} property declaration.`);
}
function normalizePropertyInput(definitions, input) {
    if (input !== undefined && !isPlainRecord(input)) {
        throw definitionError('Component property input must be a plain object', 'Pass generated component props as a plain property record.');
    }
    const known = new Set(definitions.map((item) => item.name));
    for (const key of Object.keys(input ?? {})) {
        if (!known.has(key)) {
            throw definitionError(`Component received an undeclared property: ${key}`, `Declare ${key} in Component.properties or remove it from the generated property input.`);
        }
    }
    const result = {};
    for (const definition of definitions) {
        result[definition.name] = normalizePropertyValue(definition, input === undefined ? undefined : dataValue(input, definition.name, 'Component property input').value);
    }
    return result;
}
function normalizeMethods(value) {
    if (value === undefined)
        return Object.freeze({});
    if (!isPlainRecord(value)) {
        throw definitionError('Component.methods must be a plain object', 'Declare component methods in one plain object.');
    }
    const methods = Object.create(null);
    for (const name of Object.keys(value)) {
        if (UNSAFE_KEYS.has(name) || COMPONENT_INSTANCE_KEYS.has(name))
            throw unsupportedOption(`methods.${name}`);
        const method = dataValue(value, name, 'Component.methods').value;
        if (typeof method !== 'function') {
            throw definitionError(`Component method ${name} must be a function`, `Replace methods.${name} with a function.`);
        }
        methods[name] = method;
    }
    return Object.freeze(methods);
}
function normalizeLifetimes(value) {
    if (value === undefined)
        return Object.freeze({});
    if (!isPlainRecord(value)) {
        throw definitionError('Component.lifetimes must be a plain object', 'Declare attached, ready, and detached functions.');
    }
    const lifetimes = Object.create(null);
    for (const name of Object.keys(value)) {
        if (name !== 'attached' && name !== 'ready' && name !== 'detached') {
            throw unsupportedOption(`lifetimes.${name}`);
        }
        const lifecycle = dataValue(value, name, 'Component.lifetimes').value;
        if (typeof lifecycle !== 'function') {
            throw definitionError(`Component lifecycle ${name} must be a function`, `Replace lifetimes.${name} with a function.`);
        }
        lifetimes[name] = lifecycle;
    }
    return Object.freeze(lifetimes);
}
function normalizePageLifetimes(value) {
    if (value === undefined)
        return Object.freeze({});
    if (!isPlainRecord(value)) {
        throw definitionError('Component.pageLifetimes must be a plain object', 'Declare show and hide functions in one plain object.');
    }
    const lifetimes = Object.create(null);
    for (const name of Object.keys(value)) {
        if (name !== 'show' && name !== 'hide') {
            throw unsupportedOption(`pageLifetimes.${name}`);
        }
        const lifecycle = dataValue(value, name, 'Component.pageLifetimes').value;
        if (typeof lifecycle !== 'function') {
            throw definitionError(`Component page lifecycle ${name} must be a function`, `Replace pageLifetimes.${name} with a function.`);
        }
        lifetimes[name] = lifecycle;
    }
    return Object.freeze(lifetimes);
}
function validateComponentOptions(value) {
    if (value === undefined)
        return;
    if (!isPlainRecord(value)) {
        throw definitionError('Component.options must be a plain object', 'Declare supported component options in one plain object.');
    }
    for (const key of Object.keys(value)) {
        if (key !== 'multipleSlots')
            throw unsupportedOption(`options.${key}`);
        if (typeof dataValue(value, key, 'Component.options').value !== 'boolean') {
            throw definitionError('Component.options.multipleSlots must be a boolean', 'Set multipleSlots to true or false.');
        }
    }
}
function bindMethods(instance, source) {
    const methods = Object.create(null);
    for (const [name, method] of Object.entries(source)) {
        const bound = (...args) => Reflect.apply(method, instance, args);
        methods[name] = bound;
        Object.defineProperty(instance, name, { enumerable: true, value: bound });
    }
    return Object.freeze(methods);
}
function eventOptions(value) {
    if (value === undefined) {
        return Object.freeze({ bubbles: false, capturePhase: false, composed: false });
    }
    if (!isPlainRecord(value)) {
        throw definitionError('triggerEvent options must be a plain object', 'Pass boolean bubbles, composed, and capturePhase fields.');
    }
    for (const key of Object.keys(value)) {
        if (key !== 'bubbles' && key !== 'composed' && key !== 'capturePhase') {
            throw unsupportedOption(`triggerEvent.${key}`);
        }
        if (typeof dataValue(value, key, 'triggerEvent options').value !== 'boolean') {
            throw definitionError(`triggerEvent option ${key} must be a boolean`, `Set ${key} to true or false.`);
        }
    }
    return Object.freeze({
        bubbles: value.bubbles ?? false,
        capturePhase: value.capturePhase ?? false,
        composed: value.composed ?? false,
    });
}
function normalizeRenderer(renderer) {
    if (!isPlainRecord(renderer) || typeof renderer.makeReactive !== 'function' || typeof renderer.flush !== 'function') {
        throw definitionError('Component renderer adapter requires makeReactive and flush functions', 'Use the generated Vue component renderer adapter.');
    }
    return Object.freeze({ makeReactive: renderer.makeReactive, flush: renderer.flush });
}
export function createWebComponent(options, metadata) {
    if (!isPlainRecord(options)) {
        throw definitionError('Component options must be a plain object', 'Call createWebComponent with the original option object.');
    }
    if (typeof metadata.path !== 'string' || metadata.path.length === 0) {
        throw definitionError('Component metadata path must be a non-empty string', 'Pass the normalized analyzed component path during script transformation.');
    }
    const validatedOptions = snapshotOptions(options);
    const supportedKeys = new Set(['data', 'lifetimes', 'methods', 'observers', 'options', 'pageLifetimes', 'properties']);
    for (const key of Object.keys(validatedOptions)) {
        if (!supportedKeys.has(key))
            throw unsupportedOption(key);
    }
    const properties = normalizeProperties(dataValue(validatedOptions, 'properties', 'Component options').value);
    const dataTemplate = cloneRoot(dataValue(validatedOptions, 'data', 'Component options').value ?? {}, 'Component.data');
    const methods = normalizeMethods(dataValue(validatedOptions, 'methods', 'Component options').value);
    const observers = normalizeObservers(dataValue(validatedOptions, 'observers', 'Component options').value);
    const lifetimes = normalizeLifetimes(dataValue(validatedOptions, 'lifetimes', 'Component options').value);
    const pageLifetimes = normalizePageLifetimes(dataValue(validatedOptions, 'pageLifetimes', 'Component options').value);
    validateComponentOptions(dataValue(validatedOptions, 'options', 'Component options').value);
    for (const property of properties) {
        if (Object.prototype.hasOwnProperty.call(dataTemplate, property.name)) {
            throw definitionError(`Component property conflicts with initial data: ${property.name}`, 'Use distinct names for Component.properties and Component.data fields.');
        }
    }
    const frozenMetadata = Object.freeze({ ...metadata });
    const propertyNames = Object.freeze(properties.map((property) => property.name));
    function instantiate(renderer, input, emit, flush) {
        if (emit !== undefined && typeof emit !== 'function') {
            throw definitionError('Component event emitter must be a function', 'Pass the generated Vue emit adapter.');
        }
        const normalizedRenderer = renderer === undefined ? undefined : normalizeRenderer(renderer);
        const propertyValues = normalizePropertyInput(properties, input);
        const initialData = { ...cloneRoot(dataTemplate, 'Component.data'), ...propertyValues };
        const data = normalizedRenderer?.makeReactive(initialData) ?? initialData;
        if (!isPlainRecord(data)) {
            throw definitionError('Component renderer returned an invalid data root', 'Return a plain-object-compatible reactive proxy.');
        }
        const store = createSetDataStore(data, normalizedRenderer === undefined && flush === undefined
            ? {}
            : { flush: normalizedRenderer?.flush ?? flush });
        let state = 'created';
        let pageVisible = false;
        const instance = {};
        const boundMethods = bindMethods(instance, methods);
        const applyDataPatch = (patch, callback) => {
            const previous = observers.map((observer) => snapshotObservedValue(valueAtPath(data, observer.segments), observer.path));
            store.setData(patch, callback);
            observers.forEach((observer, index) => {
                const next = valueAtPath(data, observer.segments);
                if (!sameDataValue(previous[index], next)) {
                    Reflect.apply(observer.method, instance, [next]);
                }
            });
        };
        const exposedProperties = {};
        for (const property of properties) {
            Object.defineProperty(exposedProperties, property.name, {
                enumerable: true,
                get: () => data[property.name],
            });
        }
        Object.freeze(exposedProperties);
        Object.defineProperties(instance, {
            data: { enumerable: true, value: data },
            lifecycleState: { enumerable: true, get: () => state },
            methods: { enumerable: true, value: boundMethods },
            properties: { enumerable: true, value: exposedProperties },
            setData: {
                enumerable: true,
                value(patch, callback) {
                    if (state === 'detached')
                        throw transitionError('setData', state);
                    applyDataPatch(patch, callback);
                },
            },
            triggerEvent: {
                enumerable: true,
                value(name, detail = {}, optionsValue) {
                    if (state === 'detached')
                        throw transitionError('trigger an event from', state);
                    if (typeof name !== 'string' || !/^[A-Za-z][A-Za-z0-9:_-]*$/.test(name)) {
                        throw definitionError(`Component event name is invalid: ${String(name)}`, 'Use a non-empty event name beginning with an ASCII letter.');
                    }
                    emit?.(Object.freeze({ detail: cloneValue(detail, `triggerEvent(${name}).detail`), name, options: eventOptions(optionsValue) }));
                },
            },
            updateProperties: {
                enumerable: true,
                value(next) {
                    if (state === 'detached')
                        throw transitionError('update properties on', state);
                    const normalized = normalizePropertyInput(properties, next);
                    const changed = properties.filter((property) => (!sameDataValue(data[property.name], normalized[property.name])));
                    if (changed.length === 0)
                        return;
                    const previous = Object.fromEntries(changed.map((property) => [
                        property.name,
                        cloneValue(data[property.name], `Component.properties.${property.name}.oldValue`),
                    ]));
                    applyDataPatch(Object.fromEntries(changed.map((property) => [
                        property.name,
                        normalized[property.name],
                    ])));
                    for (const property of changed) {
                        if (property.observer !== null) {
                            Reflect.apply(property.observer, instance, [
                                data[property.name],
                                previous[property.name],
                                property.name,
                            ]);
                        }
                    }
                },
            },
            whenSettled: { enumerable: true, value: () => store.whenSettled() },
            attach: {
                enumerable: true,
                value() {
                    if (state !== 'created')
                        throw transitionError('attach', state);
                    state = 'attached';
                    if (lifetimes.attached !== undefined)
                        Reflect.apply(lifetimes.attached, instance, []);
                },
            },
            pageHide: {
                enumerable: true,
                value() {
                    if ((state !== 'attached' && state !== 'ready') || !pageVisible) {
                        throw transitionError('hide component page lifetime', state);
                    }
                    pageVisible = false;
                    if (pageLifetimes.hide !== undefined)
                        Reflect.apply(pageLifetimes.hide, instance, []);
                },
            },
            pageShow: {
                enumerable: true,
                value() {
                    if ((state !== 'attached' && state !== 'ready') || pageVisible) {
                        throw transitionError('show component page lifetime', state);
                    }
                    pageVisible = true;
                    if (pageLifetimes.show !== undefined)
                        Reflect.apply(pageLifetimes.show, instance, []);
                },
            },
            ready: {
                enumerable: true,
                value() {
                    if (state !== 'attached')
                        throw transitionError('mark ready', state);
                    state = 'ready';
                    if (lifetimes.ready !== undefined)
                        Reflect.apply(lifetimes.ready, instance, []);
                },
            },
            detach: {
                enumerable: true,
                value() {
                    if (state !== 'attached' && state !== 'ready')
                        throw transitionError('detach', state);
                    state = 'detached';
                    if (lifetimes.detached !== undefined)
                        Reflect.apply(lifetimes.detached, instance, []);
                },
            },
        });
        return instance;
    }
    return Object.freeze({
        kind: 'component',
        metadata: frozenMetadata,
        options: validatedOptions,
        propertyNames,
        createInstance(propertiesInput, emit, flush) {
            return instantiate(undefined, propertiesInput, emit, flush);
        },
        createRenderedInstance(renderer, propertiesInput, emit) {
            return instantiate(renderer, propertiesInput, emit, undefined);
        },
    });
}
//# sourceMappingURL=component-factory.js.map